import { useCallback } from 'react';
import type { NastaveniSeznamApiActualDto } from '@gov-nx/api';
import {
	fetchUserSettingsQuery,
	useAxiosInstance,
	usePoQuery,
	useUnauthorizedResponse,
} from '@gov-nx/api';
import { logger } from '@gov-nx/api';
import type { PoIdentityType } from '@gov-nx/auth/common';
import { useIdentity } from '@gov-nx/auth/common';
import type { LocalizationLanguage } from '@gov-nx/core/app';
import { useAppContext } from '@gov-nx/core/app';
import type { Nullable } from '@gov-nx/core/types';
import { useUserStore } from '@gov-nx/store';
import {
	getFavoritesFromDTO,
	getRecentVisitedFromDTO,
	getSearchTermsFromDTO,
} from '@gov-nx/utils/common';
import { useUserLanguageLoader } from './userLanguageLoader';

const queryKey = ['user-settings-app-portal-obcana'];

interface UserSettingsLoader {
	persistLanguage: (language: LocalizationLanguage) => Promise<void>;
	initUserSettings: () => Promise<void>;
}

export const useUserSettingsLoader = (): UserSettingsLoader => {
	const { setAppError } = useAppContext();
	const setUserSettingFavorites = useUserStore('setUserSettingFavorites');
	const setUserSettingSearchTerms = useUserStore('setUserSettingSearchTerms');
	const setUserSettingRecentVisited = useUserStore(
		'setUserSettingRecentVisited'
	);
	const setUserSettingLanguage = useUserStore('setUserSettingLanguage');
	const poIdentityContext = useIdentity() as Nullable<PoIdentityType>;
	const { updateLanguage } = useUserLanguageLoader();
	const { isUnauthorizedResponse } = useUnauthorizedResponse();

	const persistLanguage = async (language: LocalizationLanguage) => {
		setUserSettingLanguage(language);

		if (poIdentityContext?.isAuthenticated) {
			logger('useUserSettingsLoader -> persistLanguage', [
				`${poIdentityContext.isAuthenticated}`,
			]);
			updateLanguage(language);
		}
	};
	const { axiosInstance } = useAxiosInstance();
	const initQuery = usePoQuery({
		queryFn: async (): Promise<NastaveniSeznamApiActualDto> => {
			const data = await fetchUserSettingsQuery(axiosInstance);

			const favorites = data.seznam
				? getFavoritesFromDTO(data.seznam)
				: undefined;
			if (favorites) {
				setUserSettingFavorites(favorites.value);
			}

			const searchTerms = data.seznam
				? getSearchTermsFromDTO(data.seznam)
				: undefined;
			if (searchTerms) {
				setUserSettingSearchTerms(searchTerms.value);
			}

			const recentVisited = data.seznam
				? getRecentVisitedFromDTO(data.seznam)
				: undefined;
			if (recentVisited) {
				setUserSettingRecentVisited(recentVisited.value);
			}

			return data;
		},
		onError: (error) => {
			if (!isUnauthorizedResponse(error)) {
				setAppError(error as Error);
			}
		},
		queryKey,
		enabled: false,
		retry: 1,
	});

	const fetchUserSettings = useCallback(async (): Promise<void> => {
		await initQuery.refetch();
	}, [initQuery]);

	return { initUserSettings: fetchUserSettings, persistLanguage };
};
